import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useGetWhoami } from "@/api/whoami/get-whoami";
import { AccountPopover } from "@/components/molecules/AccountPopover";
import { CreditMeter } from "@/components/molecules/CreditMeter";
import { useCurrentTenantName } from "@/hooks/use-current-tenant-name";
import { useOptionalPathTenantId } from "@/hooks/use-path-params";
import { tv } from "@/lib/tailwind-variants";

import { ConnectPrecautions } from "./connect-precautions";
import { TenantSwitcher } from "./tenant-switcher";

const variants = tv({
  slots: {
    base: "@container/main-header relative flex h-(--header-height) items-center justify-between border border-transparent border-b-gray-200 bg-white px-6",
  },
});

interface Props {
  headerStatusSlot?: React.ReactNode;
}

export const MainHeader = ({ headerStatusSlot }: Props) => {
  const { email, isHost } = useGetWhoami();
  const tenantName = useCurrentTenantName();
  const tenantId = useOptionalPathTenantId();

  const { base } = variants();

  return (
    <header className={base()}>
      <div className="flex min-w-0 items-center gap-6">
        {/* ホストは複数テナントを見て回る。名前を出すだけでなく切替も置く。
            一覧の取得に失敗しても画面全体は落とさない */}
        {isHost && tenantId ? (
          <ErrorBoundary resetKeys={[tenantId]} fallback={null}>
            <Suspense fallback={null}>
              <TenantSwitcher tenantId={tenantId} />
            </Suspense>
          </ErrorBoundary>
        ) : tenantName ? (
          <p className="shrink-0 truncate text-Medium font-bold text-gray-700">
            {tenantName}
          </p>
        ) : null}

        {headerStatusSlot}
      </div>

      <div className="flex items-center gap-4">
        {/* クレジット残量は全ロールが常時見られる場所に置く。取得はテナント配下のみ */}
        {tenantId ? (
          // 残量の取得に失敗しても画面全体は落とさない。ヘッダーは全画面で
          // 描画されるため、ここで捕まえないとテナント配下の全画面が死ぬ。
          // テナントが変われば取り直す（同一テナント内では静かに消えたまま）
          <ErrorBoundary resetKeys={[tenantId]} fallback={null}>
            <Suspense fallback={null}>
              <CreditMeter tenantId={tenantId} />
            </Suspense>
          </ErrorBoundary>
        ) : null}

        <div className="inline-flex items-center gap-1">
          <p className="text-Small text-gray-700">{email ?? ""}</p>
          <AccountPopover />
        </div>

        <ConnectPrecautions />
      </div>
    </header>
  );
};
